/**
 * AdminJournalDetailPage - 일지 상세 조회 (읽기 전용)
 *
 * /centers/:centerId/instructors/:instructorId/journals/:journalId 경로로 접근하며,
 * 선택한 일지의 상세 내용을 읽기 전용으로 표시한다.
 */

import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useCurrentUser } from "@/components/AuthProvider";
import { getJournalById, type AdminJournalDocument } from "@/services/firestore/admin-journals";

const EQUIPMENT_LABELS: Record<string, string> = {
  reformer: "리포머",
  cadillac: "캐딜락",
  chair: "체어",
  barrel: "배럴",
  smallEquipment: "소도구",
  mat: "매트",
};

function getClassTypeLabel(classType: string | undefined, isOt: boolean | undefined): string {
  if (!classType) return "";
  if (classType === "PRIVATE_REGULAR") return isOt ? "1:1 OT" : "1:1 정규";
  if (classType === "GROUP_REGULAR") return isOt ? "그룹 OT" : "그룹 정규";
  return classType;
}

export function AdminJournalDetailPage() {
  const { centerId, instructorId, journalId } = useParams<{
    centerId: string;
    instructorId: string;
    journalId: string;
  }>();
  const user = useCurrentUser();

  const [journal, setJournal] = useState<AdminJournalDocument | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchJournal() {
      if (!centerId || !journalId) return;

      setIsLoading(true);
      setError(null);

      try {
        const result = await getJournalById(user.uid, Number(centerId), journalId);

        if (!cancelled) {
          setJournal(result);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error("일지 데이터 조회 실패"));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchJournal();

    return () => {
      cancelled = true;
    };
  }, [user.uid, centerId, journalId]);

  const backLink = `/centers/${centerId}/instructors/${instructorId}/journals`;

  // --- 로딩 상태 ---
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Link to={backLink} className="text-blue-600 hover:text-blue-800 text-sm">
            &larr; 일지 목록
          </Link>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      </div>
    );
  }

  // --- 에러 상태 ---
  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <Link to={backLink} className="text-blue-600 hover:text-blue-800 text-sm">
            &larr; 일지 목록
          </Link>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-700">오류 발생</h2>
          <p className="text-red-600 mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  // --- 일지를 찾을 수 없는 상태 ---
  if (!journal) {
    return (
      <div className="space-y-6">
        <div>
          <Link to={backLink} className="text-blue-600 hover:text-blue-800 text-sm">
            &larr; 일지 목록
          </Link>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-yellow-700">일지를 찾을 수 없습니다</h2>
          <p className="text-yellow-600 mt-1">요청하신 일지가 존재하지 않거나 삭제되었습니다.</p>
        </div>
      </div>
    );
  }

  const createdDate = journal.createdAt?.toDate?.();
  const dateStr = createdDate ? createdDate.toLocaleDateString("ko-KR") : "-";
  const updatedDate = journal.updatedAt?.toDate?.();
  const updatedStr = updatedDate ? updatedDate.toLocaleDateString("ko-KR") : null;

  return (
    <div className="space-y-6">
      {/* 뒤로가기 링크 */}
      <div className="flex items-center gap-4">
        <Link to={backLink} className="text-blue-600 hover:text-blue-800 text-sm">
          &larr; 일지 목록
        </Link>
        {/* studiomateMemberId 또는 memberId가 있을 때만 회원 상세 링크 표시 */}
        {(journal.studiomateMemberId ?? journal.memberId) && (
          <Link
            to={`/centers/${centerId}/members/${journal.studiomateMemberId ?? journal.memberId}`}
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            회원 상세 &rarr;
          </Link>
        )}
      </div>

      {/* 헤더 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">일지 상세</h1>
          <span className="text-sm text-gray-500">읽기 전용</span>
        </div>
        <div className="mt-2 flex items-center gap-3 text-sm text-gray-500">
          <span>작성일: {dateStr}</span>
          {updatedStr && <span>수정일: {updatedStr}</span>}
          <span>수업 ID: {journal.classId}</span>
        </div>
      </div>

      {/* 수업 유형 */}
      {journal.classType && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-sm font-semibold text-gray-500 mb-2">수업 유형</h2>
          <span className="inline-block px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800">
            {getClassTypeLabel(journal.classType, journal.isOt)}
          </span>
        </div>
      )}

      {/* 사용 기구 */}
      {journal.equipments.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-sm font-semibold text-gray-500 mb-2">사용 기구</h2>
          <div className="flex flex-wrap gap-2">
            {journal.equipments.map((eq) => (
              <span
                key={eq}
                className="inline-block px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg"
              >
                {EQUIPMENT_LABELS[eq] ?? eq}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 수업 시퀀스 */}
      {journal.exercises.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-sm font-semibold text-gray-500 mb-2">
            수업 시퀀스 ({journal.exercises.length}개)
          </h2>
          <ol className="list-decimal list-inside space-y-1">
            {journal.exercises.map((ex, idx) => (
              <li key={idx} className="text-sm text-gray-800">
                {ex.name}
                {ex.notes && <span className="text-gray-500 ml-2">- {ex.notes}</span>}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* 문제 목록(Problem List) */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-sm font-semibold text-gray-500 mb-2">문제 목록(Problem List)</h2>
        <p className="text-sm text-gray-800 whitespace-pre-wrap">{journal.problemList}</p>
      </div>

      {/* 특이사항 */}
      {journal.notes && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-sm font-semibold text-gray-500 mb-2">특이사항</h2>
          <p className="text-sm text-gray-800 whitespace-pre-wrap">{journal.notes}</p>
        </div>
      )}

      {/* OT 전용 필드 */}
      {journal.isOt && (
        <>
          {journal.bodyAnalysis && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-sm font-semibold text-gray-500 mb-2">체형 분석</h2>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{journal.bodyAnalysis}</p>
            </div>
          )}
          {journal.memberGoal && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-sm font-semibold text-gray-500 mb-2">회원 목표</h2>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{journal.memberGoal}</p>
            </div>
          )}
          {journal.instructorMemo && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-sm font-semibold text-gray-500 mb-2">강사 메모</h2>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{journal.instructorMemo}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
